import { useCallback } from "react";

import { ApolloCache } from "@apollo/client";

import {
  MessageFragment,
  MessageRequestType,
  useSendMessageMutation,
  useUpdateGroupChangeRequestMutation,
  useWithdrawGroupChangeRequestMutation,
} from "@/generated/graphql";

// Single source of truth for how every write to a work group's request keeps
// the `WorkGroup` cache entry in sync, so `send`/`update`/`withdraw` below
// can't drift from one another on how they patch it.
//
// Writes a normalized reference (via `toReference`) rather than the raw
// `Message` object, so this field points at the same cache entity as any
// other read of the same Message — an embedded plain object wouldn't pick up
// updates made elsewhere.
function writeChangeRequest(
  cache: ApolloCache<unknown>,
  workGroupId: string,
  value: MessageFragment | null
) {
  cache.modify({
    id: `WorkGroup:${workGroupId}`,
    fields: {
      latestGroupChangeRequest: (_existing, { toReference }) =>
        value ? toReference(value) : null,
    },
  });
}

export function useGroupChangeRequest(assessmentId: string) {
  const [sendMessage] = useSendMessageMutation();
  const [updateGroupChangeRequest] = useUpdateGroupChangeRequestMutation();
  const [withdrawGroupChangeRequest] = useWithdrawGroupChangeRequestMutation();

  const send = useCallback(
    (workGroupId: string, reason: string) =>
      sendMessage({
        variables: {
          assessmentId,
          message: {
            messageType: MessageRequestType.GroupChange,
            targetUserId: null,
            reason,
          },
        },
        update: (cache, { data }) => {
          if (data?.sendMessage) {
            writeChangeRequest(cache, workGroupId, data.sendMessage);
          }
        },
      }),
    [sendMessage, assessmentId]
  );

  const update = useCallback(
    (workGroupId: string, messageId: string, reason: string) =>
      updateGroupChangeRequest({
        variables: { assessmentId, messageId, reason },
        update: (cache, { data }) => {
          if (data?.updateGroupChangeRequest != null) {
            writeChangeRequest(cache, workGroupId, data.updateGroupChangeRequest);
          }
        },
      }),
    [updateGroupChangeRequest, assessmentId]
  );

  const withdraw = useCallback(
    (workGroupId: string, messageId: string) =>
      withdrawGroupChangeRequest({
        variables: { assessmentId, messageId },
        update: (cache) => {
          writeChangeRequest(cache, workGroupId, null);
        },
      }),
    [withdrawGroupChangeRequest, assessmentId]
  );

  return { send, update, withdraw };
}
